import React, { useState, useRef } from "react";
import { uploadRecipientAttachments } from "../api/signRequests";

const DocumentUploadModal = ({
  isOpen,
  onClose,
  onUploadComplete,
  signRequestId,
  recipientContactId,
  documentRequiredMessage,
}) => {
  const [files, setFiles] = useState([]);
  const [descriptions, setDescriptions] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const fileInputRef = useRef(null);

  const isValidFileType = (file) => {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/bmp",
      "image/tiff",
    ];
    return allowedTypes.includes(file.type);
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Filter out invalid file types
    const validFiles = selectedFiles.filter((file) => {
      if (!isValidFileType(file)) {
        alert(
          `File "${file.name}" is not supported. Please upload only PDF and image files.`
        );
        return false;
      }
      return true;
    });

    const newFiles = [...files, ...validFiles].slice(0, 10); // Max 10 files
    setFiles(newFiles);

    // Initialize descriptions for new files
    const newDescriptions = { ...descriptions };
    validFiles.forEach((file) => {
      if (!newDescriptions[file.name]) {
        newDescriptions[file.name] = "";
      }
    });
    setDescriptions(newDescriptions);
  };

  const removeFile = (fileName) => {
    setFiles(files.filter((file) => file.name !== fileName));
    const newDescriptions = { ...descriptions };
    delete newDescriptions[fileName];
    setDescriptions(newDescriptions);
  };

  const updateDescription = (fileName, description) => {
    setDescriptions((prev) => ({
      ...prev,
      [fileName]: description,
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      alert("Please select at least one file to upload");
      return;
    }

    setIsUploading(true);
    setUploadedFiles([]);
    setAllUploadsComplete(false);

    try {
      const formData = new FormData();

      // Add files
      files.forEach((file) => {
        formData.append("files", file);
      });

      // Add descriptions as JSON string
      const fileDescriptions = files.map(
        (file) => descriptions[file.name] || ""
      );
      formData.append("fileDescriptions", JSON.stringify(fileDescriptions));

      // Upload files one by one to show individual progress
      const uploadedFileList = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const singleFileFormData = new FormData();
        singleFileFormData.append("files", file);
        singleFileFormData.append(
          "fileDescriptions",
          JSON.stringify([descriptions[file.name] || ""])
        );

        // Update progress for current file
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: { status: "uploading", progress: 0 },
        }));

        // Simulate individual file progress
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: {
              status: "uploading",
              progress: Math.min(
                prev[file.name]?.progress + Math.random() * 20,
                90
              ),
            },
          }));
        }, 200);

        try {
          await uploadRecipientAttachments(
            signRequestId,
            recipientContactId,
            singleFileFormData
          );

          clearInterval(progressInterval);

          // Mark file as completed
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: { status: "completed", progress: 100 },
          }));

          uploadedFileList.push({
            name: file.name,
            size: file.size,
            description: descriptions[file.name] || "",
          });

          setUploadedFiles([...uploadedFileList]);

          // Small delay between files for better UX
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (fileError) {
          clearInterval(progressInterval);
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: { status: "error", progress: 0 },
          }));
          throw fileError;
        }
      }

      setAllUploadsComplete(true);
      setIsUploading(false);
    } catch (error) {
      console.error("Upload error:", error);
      setIsUploading(false);
      alert("Failed to upload files. Please try again.");
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setDescriptions({});
      setUploadProgress({});
      setUploadedFiles([]);
      setAllUploadsComplete(false);
      onClose();
    }
  };

  const handleNext = () => {
    onUploadComplete({ success: true, uploadedFiles });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg
                  className="h-6 w-6 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Upload Required Documents
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    {documentRequiredMessage ||
                      "Please upload the required documents to proceed with signing."}
                  </p>

                  {/* File Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      allUploadsComplete
                        ? "border-gray-200 bg-gray-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.tif"
                      disabled={allUploadsComplete}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        files.length >= 10 || isUploading || allUploadsComplete
                      }
                      className={`font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                        allUploadsComplete
                          ? "text-gray-400"
                          : "text-blue-600 hover:text-blue-500"
                      }`}
                    >
                      <svg
                        className={`mx-auto h-12 w-12 mb-4 ${
                          allUploadsComplete ? "text-gray-300" : "text-gray-400"
                        }`}
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {allUploadsComplete
                        ? "All files uploaded successfully"
                        : "Click to upload files or drag and drop"}
                    </button>
                    <p
                      className={`text-xs mt-2 ${
                        allUploadsComplete ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {allUploadsComplete
                        ? "Ready to proceed to signing"
                        : "Maximum 10 files. Supported formats: PDF, JPG, PNG, GIF, WebP"}
                    </p>
                  </div>

                  {/* File List */}
                  {files.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Selected Files ({files.length}/10)
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {files.map((file, index) => {
                          const fileProgress = uploadProgress[file.name];
                          const isCompleted =
                            fileProgress?.status === "completed";
                          const isUploading =
                            fileProgress?.status === "uploading";
                          const hasError = fileProgress?.status === "error";

                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  {isCompleted && (
                                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-4 h-4 text-green-600"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                  {hasError && (
                                    <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-4 h-4 text-red-600"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0 text-left">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>

                                {/* Progress bar for uploading files */}
                                {isUploading && (
                                  <div className="mt-2">
                                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                      <span>Uploading...</span>
                                      <span>
                                        {Math.round(fileProgress.progress)}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1">
                                      <div
                                        className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                                        style={{
                                          width: `${fileProgress.progress}%`,
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                )}

                                <input
                                  type="text"
                                  placeholder="Enter description (optional)"
                                  value={descriptions[file.name] || ""}
                                  onChange={(e) =>
                                    updateDescription(file.name, e.target.value)
                                  }
                                  className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  disabled={isUploading}
                                />
                              </div>
                              {!isCompleted && (
                                <button
                                  onClick={() => removeFile(file.name)}
                                  disabled={isUploading}
                                  className="ml-2 p-1 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {allUploadsComplete ? (
              <button
                type="button"
                onClick={handleNext}
                className="w-full inline-flex justify-center items-center gap-2 rounded-lg border border-transparent shadow-sm px-4 py-2.5 bg-green-600 hover:bg-green-700 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={uploadFiles}
                disabled={files.length === 0 || isUploading}
                className="w-full inline-flex justify-center items-center gap-2 rounded-lg border border-transparent shadow-sm px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200"
              >
                {isUploading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Upload Files
                  </>
                )}
              </button>
            )}
            {!allUploadsComplete && (
              <button
                type="button"
                onClick={handleClose}
                disabled={isUploading}
                className="mt-3 w-full inline-flex justify-center items-center gap-2 rounded-lg border border-gray-300 shadow-sm px-4 py-2.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
